/* Greatly Social – InstagramGrid.js  (Calendar › Instagram grid, Part E, T30)
   ----------------------------------------------------------------
   Grid.dc.html: one Instagram account's profile as it will look, with the
   scheduled posts (blue dashed outline + time badge) on top of what is
   already published, in Instagram's 4:5 portrait grid.
   • Posts / Reels tabs (Reels = scheduled and published Reels, with covers)
   • drag a scheduled post onto another one (or Alt + ← / →) to reorder:
     POST /api/instagram-grid/reorder swaps their posting times so the
     grid goes out the way it looks
   • click a scheduled post → the composer's edit flow; a published one
     opens on Instagram
   Data: GET /api/instagram-grid (docs/MOBILE_API.md › Instagram grid).
   ---------------------------------------------------------------- */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import moment from 'moment-timezone'

const ROOT     = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '')
const API_BASE = ROOT ? `${ROOT}/api` : '/api'
const ax = axios.create({ withCredentials: true })

const BLUE = '#2563eb'

/* ───────── helpers (exported for tests) ───────── */

/* The grid order after dropping `dragId` on `targetId`: the dragged post
   takes the target's place and the ones in between shift along. */
export function moveTile (ids, dragId, targetId) {
  const from = ids.indexOf(dragId)
  const to   = ids.indexOf(targetId)
  if (from < 0 || to < 0 || from === to) return ids
  const out = ids.slice()
  out.splice(from, 1)
  out.splice(to, 0, dragId)
  return out
}

/* The time badge: 'Thu 9:30' this week, '12 Nov 9:30' further out. */
export function badgeTime (iso, tz, nowMs = Date.now()) {
  const m = moment.utc(iso).tz(tz || moment.tz.guess())
  return m.diff(moment(nowMs), 'days', true) < 6 ? m.format('ddd H:mm') : m.format('D MMM H:mm')
}

/* Puts the reordered scheduled tiles (with their new times) back in the list. */
function applyOrder (tiles, order, times) {
  const byId = new Map(tiles.filter(t => t.type === 'scheduled').map(t => [t.id, t]))
  const moved = order.map(id => ({ ...byId.get(id), scheduledAt: times.get(id) || byId.get(id).scheduledAt }))
  const set = new Set(order)
  let i = 0
  return tiles.map(t => (t.type === 'scheduled' && set.has(t.id) ? moved[i++] : t))
}

/* ───────── tiles ───────── */
const tileBase = {
  aspectRatio: '4 / 5', position: 'relative', overflow: 'hidden',
  background: 'linear-gradient(160deg, #e5e7eb, #cbd5e1)', border: 0, padding: 0,
  display: 'block', width: '100%', cursor: 'pointer',
}
const fill = { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }
const kindIcon = { reel: 'fas fa-play', video: 'fas fa-play', carousel: 'fas fa-clone' }

function Picture ({ tile }) {
  if (tile.coverUrl) return <img src={tile.coverUrl} alt='' style={fill} draggable={false} />
  if (tile.mediaUrl && tile.isVideo) return <video src={`${tile.mediaUrl}#t=0.5`} muted preload='metadata' style={fill} />
  return (
    <span style={{ ...fill, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
      <i className='fas fa-file-alt' aria-hidden='true' />
    </span>
  )
}

function KindIcon ({ kind }) {
  if (!kindIcon[kind]) return null
  return (
    <i className={kindIcon[kind]} aria-hidden='true'
      style={{ position: 'absolute', top: 6, right: 6, fontSize: 11, color: '#fff', textShadow: '0 0 4px rgba(0,0,0,0.6)' }} />
  )
}

/* ═══════════════════════════════════════════════════════════
   Component
═══════════════════════════════════════════════════════════ */
export default function InstagramGrid ({ accountId, onAccounts, onAccountMissing, userTz }) {
  const navigate = useNavigate()
  const [data,    setData]    = useState(null)
  const [error,   setError]   = useState('')
  const [loading, setLoading] = useState(true)
  const [tab,     setTab]     = useState('posts')
  const [dragId,  setDragId]  = useState(null)
  const [overId,  setOverId]  = useState(null)
  const [saving,  setSaving]  = useState(false)
  const [notice,  setNotice]  = useState(null)   // { ok, text }

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const { data: d } = await ax.get(`${API_BASE}/instagram-grid`, { params: accountId ? { accountId } : {} })
      setData(d)
      if (onAccounts) onAccounts(d.accounts || [], d.account ? d.account.id : '')
    } catch (err) {
      // A remembered account that has since been removed: show the first one instead.
      if (accountId && err?.response?.data?.error === 'account_not_found' && onAccountMissing) return onAccountMissing()
      setError(err?.response?.data?.message || 'Could not load your Instagram grid. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [accountId, onAccounts, onAccountMissing])

  useEffect(() => { load() }, [load])

  const tiles = useMemo(() => (data ? (tab === 'reels' ? data.reels : data.posts) || [] : []), [data, tab])
  const movableIds = useMemo(() => tiles.filter(t => t.type === 'scheduled' && t.movable).map(t => t.id), [tiles])

  /* reorder: optimistic, then the server's times */
  const reorder = async (dragged, target) => {
    const order = moveTile(movableIds, dragged, target)
    if (order === movableIds || saving) return
    const before = data
    // Show the new order straight away, with the times the server will
    // hand out (the same times, latest first); its answer then confirms them.
    const listKey = tab === 'reels' ? 'reels' : 'posts'
    const times = tiles.filter(t => t.type === 'scheduled' && order.includes(t.id)).map(t => t.scheduledAt)
      .sort((a, b) => Date.parse(b) - Date.parse(a))
    setData(d => ({ ...d, [listKey]: applyOrder(d[listKey], order, new Map(order.map((id, i) => [id, times[i]]))) }))
    setSaving(true)
    setNotice(null)
    try {
      const { data: res } = await ax.post(`${API_BASE}/instagram-grid/reorder`, { accountId: data.account.id, order })
      const confirmed = new Map((res.posts || []).map(p => [p.id, p.scheduledAt]))
      setData(d => ({ ...d, posts: applyTimes(d.posts, confirmed), reels: applyTimes(d.reels, confirmed) }))
      if (res.moved) load()   // the other tab's order changes too (Reels sit among the photos): fetch it fresh
      setNotice({ ok: true, text: res.moved ? 'Done — the posting times swapped to match your grid.' : 'Already in that order.' })
    } catch (err) {
      setData(before)
      setNotice({ ok: false, text: err?.response?.data?.message || 'Could not reorder these posts. Please try again.' })
    } finally {
      setSaving(false)
    }
  }

  const onKey = (e, tile) => {
    if (!e.altKey || !tile.movable) return
    const i = movableIds.indexOf(tile.id)
    const j = e.key === 'ArrowLeft' ? i - 1 : e.key === 'ArrowRight' ? i + 1 : -1
    if (j < 0 || j >= movableIds.length) return
    e.preventDefault()
    reorder(tile.id, movableIds[j])
  }

  const scheduledTile = (t) => {
    const time = badgeTime(t.scheduledAt, userTz)
    const isDropTarget = dragId && overId === t.id && dragId !== t.id
    return (
      <button
        key={`s${t.id}`}
        type='button'
        data-testid='grid-tile-scheduled'
        aria-label={`Scheduled ${t.kind === 'reel' ? 'Reel' : 'post'}, ${time}. Click to edit${t.movable ? '; drag, or Alt + arrow keys, to reorder' : ''}`}
        draggable={t.movable && !saving}
        onDragStart={e => { setDragId(t.id); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(t.id)) } catch {} }}
        onDragEnd={() => { setDragId(null); setOverId(null) }}
        onDragOver={e => { if (dragId && t.movable) { e.preventDefault(); setOverId(t.id) } }}
        onDragLeave={() => setOverId(o => (o === t.id ? null : o))}
        onDrop={e => { e.preventDefault(); const d = dragId; setDragId(null); setOverId(null); if (d && t.movable) reorder(d, t.id) }}
        onKeyDown={e => onKey(e, t)}
        onClick={() => navigate('/dashboard/post', { state: { editPostId: t.id } })}
        style={{
          ...tileBase,
          outline: `2px dashed ${BLUE}`, outlineOffset: -2,
          opacity: dragId === t.id ? 0.45 : 1,
          boxShadow: isDropTarget ? `inset 0 0 0 4px rgba(37,99,235,0.55)` : 'none',
          cursor: t.movable ? 'grab' : 'pointer',
        }}
      >
        <Picture tile={t} />
        <KindIcon kind={t.kind} />
        <span style={{
          position: 'absolute', left: 4, bottom: 4, fontSize: 10, fontWeight: 700, color: '#fff',
          background: 'rgba(37,99,235,0.92)', padding: '2px 5px', borderRadius: 4,
        }}>
          {t.status === 'processing' ? 'Posting…' : time}
        </span>
      </button>
    )
  }

  const publishedTile = (t) => {
    const inner = (<><Picture tile={t} /><KindIcon kind={t.kind} /></>)
    const label = `Published ${t.kind === 'reel' ? 'Reel' : 'post'}${t.caption ? `: ${t.caption.slice(0, 60)}` : ''}`
    return t.permalink ? (
      <a key={`p${t.id}`} href={t.permalink} target='_blank' rel='noopener noreferrer' aria-label={label}
        data-testid='grid-tile-published' style={tileBase}>{inner}</a>
    ) : (
      <div key={`p${t.id}`} role='img' aria-label={label} data-testid='grid-tile-published' style={{ ...tileBase, cursor: 'default' }}>{inner}</div>
    )
  }

  const account   = data && data.account
  const published = data && data.published
  const noAccount = data && !account
  const nothingPublished = published && published.state === 'ok' && published.count === 0

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>

      {/* Profile preview */}
      <div style={{
        width: 420, maxWidth: '100%', flex: 'none', boxSizing: 'border-box', background: '#ffffff',
        border: '1px solid #e5e7eb', borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        {loading && !data && <div style={{ padding: 24, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>Loading your grid…</div>}

        {error && (
          <div role='alert' style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#dc2626' }}>
            {error} <button type='button' onClick={load} style={{ border: 0, background: 'none', color: BLUE, fontWeight: 600, cursor: 'pointer', padding: 0 }}>Try again</button>
          </div>
        )}

        {noAccount && (
          <div style={{ padding: '20px 8px', textAlign: 'center' }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>No Instagram account connected</div>
            <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 14 }}>{published.message}</div>
            <Link to='/dashboard/accounts' style={{ padding: '8px 16px', borderRadius: 8, background: BLUE, color: '#fff', fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>
              Connect Instagram
            </Link>
          </div>
        )}

        {account && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {account.avatar
                ? <img src={account.avatar} alt='' style={{ width: 56, height: 56, borderRadius: 28, objectFit: 'cover' }} />
                : <div style={{ width: 56, height: 56, borderRadius: 28, background: 'linear-gradient(135deg, #f58529, #dd2a7b, #8134af)' }} />}
              <div>
                <div style={{ fontWeight: 700 }}>{account.username || 'Instagram account'}</div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>Preview of your profile with scheduled posts</div>
              </div>
            </div>

            <div role='tablist' aria-label='Grid' style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
              {[['posts', 'Posts'], ['reels', 'Reels']].map(([key, label]) => (
                <button key={key} type='button' role='tab' aria-selected={tab === key} onClick={() => setTab(key)}
                  style={{
                    flex: 1, padding: 8, border: 0, background: 'none', font: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    color: tab === key ? '#111827' : '#6b7280', borderBottom: tab === key ? '2px solid #111827' : '2px solid transparent',
                  }}>
                  {label}
                </button>
              ))}
            </div>

            {published && published.message && (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#92400e' }}>
                {published.message}
                {published.state === 'needs_reconnect' && <> <Link to='/dashboard/accounts' style={{ fontWeight: 600 }}>Reconnect</Link></>}
              </div>
            )}

            {tiles.length ? (
              <div role='list' aria-label={tab === 'reels' ? 'Reels' : 'Posts'}
                style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 3 }}>
                {tiles.map(t => <div role='listitem' key={`${t.type}${t.id}`}>{t.type === 'scheduled' ? scheduledTile(t) : publishedTile(t)}</div>)}
              </div>
            ) : (
              <div style={{ padding: '28px 8px', textAlign: 'center', fontSize: 14, color: '#6b7280' }}>
                {tab === 'reels' ? 'No Reels yet. Schedule a video to see it here.' : 'Nothing here yet. Schedule a post to see how your grid will look.'}
              </div>
            )}

            {nothingPublished && tiles.length > 0 && (
              <div style={{ fontSize: 13, color: '#6b7280' }}>Nothing is published on this account yet, so you’re seeing only your scheduled posts.</div>
            )}

            {notice && (
              <div role='status' style={{ fontSize: 13, fontWeight: 600, color: notice.ok ? '#16a34a' : '#dc2626' }}>{notice.text}</div>
            )}
          </>
        )}
      </div>

      {/* Explainer */}
      <div style={{ flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>See your feed before it goes live</div>
          <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.5 }}>
            Your scheduled Instagram posts (blue dashed outline) appear on top of what's already on your profile, in Instagram's new 4:5 portrait grid.
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, color: '#374151', lineHeight: 1.7 }}>
            <li><strong>Drag to reorder.</strong> The posting times swap so the grid goes out the way you see it.</li>
            <li><strong>Click a post</strong> to edit its caption, photo or time.</li>
            <li><strong>Reels tab</strong> shows your scheduled Reels with their covers.</li>
          </ul>
        </div>
        <div style={{ background: '#eff6ff', border: '1px solid #dbeafe', borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, color: '#1e40af', flex: 1 }}><strong>Tip:</strong> Planning a new look? Try 3 posts in a row that share a colour.</span>
          <Link to='/dashboard/post' style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #dbeafe', background: '#ffffff', color: BLUE, fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>
            + Schedule a post
          </Link>
        </div>
      </div>
    </div>
  )
}

function applyTimes (tiles, times) {
  return (tiles || []).map(t => (t.type === 'scheduled' && times.has(t.id) ? { ...t, scheduledAt: times.get(t.id) } : t))
}
